import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Check } from "lucide-react";

const propositions = ["Titulares RETEF", "Respuesta en 24h", "Operativa nacional e internacional"];

const cards = [
  { n: "01", label: "Hidrocarburos", href: "/sectores/hidrocarburos" },
  { n: "02", label: "Alcohol", href: "/sectores/alcohol" },
  { n: "03", label: "Bebidas Derivadas", href: "/#productos" },
  { n: "04", label: "Trading UE", href: "/#servicios" },
];

export const Hero = () => {
  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden pt-24 pb-28"
    >
      <div className="absolute inset-0 bg-gradient-to-br from-[#0A1628] via-[#0E1F38] to-[#0A1628]" />
      <div className="absolute top-32 -left-20 w-[480px] h-[480px] rounded-full bg-[#0066FF]/20 blur-[120px] animate-float" />
      <div className="absolute bottom-10 right-0 w-[520px] h-[520px] rounded-full bg-[#FF6B00]/15 blur-[140px] animate-float-rev" />
      <div
        className="absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full grid lg:grid-cols-12 gap-10 items-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="lg:col-span-7"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm mb-6">
            <ShieldCheck className="w-4 h-4 text-[#FF6B00]" />
            <span className="text-[11px] uppercase tracking-[0.2em] text-slate-300 font-medium">
              Titulares del RETEF · Real Decreto 249/2023
            </span>
          </div>

          <h1
            className="text-3xl sm:text-4xl lg:text-5xl xl:text-[3.4rem] font-bold text-white leading-[1.08] tracking-tight"
            style={{ fontFamily: "Poppins, sans-serif" }}
          >
            Especialistas en{" "}
            <span className="relative inline-block">
              <span className="relative z-10 bg-gradient-to-r from-[#0066FF] to-[#3B8AFF] bg-clip-text text-transparent">
                impuestos especiales
              </span>
              <span className="absolute -bottom-1 left-0 right-0 h-2 bg-[#FF6B00]/20 -z-0 rounded" />
            </span>{" "}
            sobre alcohol, bebidas derivadas e hidrocarburos.
          </h1>

          <p className="mt-6 text-base md:text-lg text-slate-300 max-w-2xl leading-relaxed">
            Somos extractores autorizados inscritos en el REDEF concedido por
            la AEAT. Gestionamos tu operativa en depósitos fiscales con total
            seguridad jurídica, liberando la liquidez retenida por el régimen
            suspensivo.
          </p>

          <div className="mt-8 flex flex-wrap gap-4">
            <a
              href="#servicios"
              data-testid="hero-services-btn"
              className="inline-flex items-center gap-2 bg-[#0066FF] hover:bg-[#0052CC] text-white font-medium px-6 py-3.5 rounded-xl transition-all shadow-xl shadow-[#0066FF]/30 hover:shadow-[#0066FF]/50 hover:-translate-y-0.5"
            >
              Conoce nuestros servicios
              <ArrowRight className="w-5 h-5" />
            </a>
            <a
              href="#contacto"
              data-testid="hero-contact-btn"
              className="inline-flex items-center gap-2 bg-transparent border border-[#FF6B00]/70 text-[#FF6B00] hover:bg-[#FF6B00] hover:text-white font-medium px-6 py-3.5 rounded-xl transition-all"
            >
              Contáctanos
            </a>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="hidden lg:block lg:col-span-5"
        >
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-[#0066FF]/30 to-[#FF6B00]/20 blur-3xl" />
            <div className="relative bg-[#1E3A5F]/40 backdrop-blur-md border border-white/10 rounded-3xl p-6">
              <div className="grid grid-cols-2 gap-3">
                {cards.map((c) => (
                  <a
                    key={c.n}
                    href={c.href}
                    data-testid={`hero-card-${c.n}`}
                    className="group aspect-square rounded-2xl bg-gradient-to-br from-white/5 to-white/0 border border-white/10 p-4 flex flex-col justify-between cursor-pointer hover:border-[#0066FF] hover:bg-[#0066FF]/5 hover:-translate-y-1 transition-all"
                  >
                    <span className="text-2xl font-bold text-[#0066FF] group-hover:text-[#3B8AFF]" style={{ fontFamily: "Poppins" }}>
                      {c.n}
                    </span>
                    <span className="text-sm text-slate-200 leading-tight font-medium">{c.label}</span>
                  </a>
                ))}
              </div>
              <div className="mt-5 grid grid-cols-2 gap-3">
                <a
                  href="/#retef"
                  data-testid="hero-pill-suspensivo"
                  className="p-3 rounded-xl bg-[#0A1628]/60 border border-white/5 hover:border-[#0066FF]/60 cursor-pointer transition-all"
                >
                  <div className="text-[10px] uppercase tracking-widest text-slate-400">Régimen</div>
                  <div className="text-white font-semibold mt-0.5 text-sm">Suspensivo IIEE</div>
                </a>
                <a
                  href="https://www.boe.es/buscar/act.php?id=BOE-A-1992-26834"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hero-pill-ley"
                  className="p-3 rounded-xl bg-[#0A1628]/60 border border-white/5 hover:border-[#FF6B00]/60 cursor-pointer transition-all"
                >
                  <div className="text-[10px] uppercase tracking-widest text-slate-400">Marco</div>
                  <div className="text-white font-semibold mt-0.5 text-sm">Ley 38/1992 ↗</div>
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 border-t border-white/10 bg-[#0A1628]/60 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 grid grid-cols-1 md:grid-cols-3 gap-4">
          {propositions.map((p, i) => (
            <div key={p} className="flex items-center gap-3" data-testid={`hero-value-${i}`}>
              <span className="flex-shrink-0 w-9 h-9 rounded-full bg-[#0066FF]/15 border border-[#0066FF]/40 flex items-center justify-center">
                <Check className="w-5 h-5 text-[#0066FF]" strokeWidth={2.6} />
              </span>
              <span className="text-base font-medium text-white" style={{ fontFamily: "Poppins" }}>
                {p}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
